/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Button, LinearProgress, Tab, Tabs } from '@mui/material';
import React from 'react';
import type { HeaderSize, Position, DraggableSize } from '@opengeoweb/shared';
import {
  AlertBanner,
  ToolContainerDraggable,
  calculateStartSize,
} from '@opengeoweb/shared';
import type { uiTypes } from '@opengeoweb/store';
import { DrawPolygon } from '@opengeoweb/theme';
import { Areas } from './Areas';
import type { SelectedAreaFeatureNamesByAreaId } from './Areas';
import {
  Objects,
  DEFAULT_OBJECT_MANAGER_TAB_HEIGHT,
  DEFAULT_OBJECT_MANAGER_MIN_SIZE,
} from './Objects';
import type {
  AreaListItem,
  DrawingListItem,
} from '../../store/warningsDrawings/types';
import { useWarningsTranslation } from '../../utils/i18n';

const DEFAULT_OBJECT_MANAGER_SIZE = { width: 320, height: 542 };
const DEFAULT_OBJECT_MANAGER_POSITION = { top: 120, left: 50 };

const tabStyle = {
  '&.MuiButtonBase-root.MuiTab-root': {
    minHeight: `${DEFAULT_OBJECT_MANAGER_TAB_HEIGHT}px`,
    fontSize: '12px',
    pt: '8px',
    pb: '0px',
  },
};

export const ObjectManager: React.FC<{
  bounds?: string;
  title?: string;
  onClose: () => void;
  onMouseDown?: () => void;
  isOpen: boolean;
  order?: number;
  source?: uiTypes.Source;
  size?: DraggableSize;
  startPosition?: Position;
  headerSize?: HeaderSize;
  drawingListItems: DrawingListItem[];
  predefinedAreaListItems: AreaListItem[];
  onClickDrawing?: (drawing: DrawingListItem) => void;
  onClickArea?: (area: AreaListItem) => void;
  onHoverArea?: (area: AreaListItem) => void;
  onHoverAreaEnd?: () => void;
  onEditDrawing?: (drawingId: string) => void;
  onDeleteDrawing?: (drawingId: string) => void;
  onShareObject?: (object: DrawingListItem) => void;
  onCreateNewObject?: () => void;
  activeDrawingId?: string;
  selectedAreaFeatureNamesByAreaId?: SelectedAreaFeatureNamesByAreaId;
  error?: string;
  isLoading?: boolean;
  selectedTab?: number;
  onChangeTab?: (tab: number) => void;
}> = ({
  bounds,
  onClose,
  title = 'Area & Object Manager',
  isOpen,
  onMouseDown = (): void => {},
  order = 0,
  source = 'app',
  size = DEFAULT_OBJECT_MANAGER_SIZE,
  startPosition = DEFAULT_OBJECT_MANAGER_POSITION,
  headerSize = 'xs',
  drawingListItems,
  predefinedAreaListItems,
  onClickArea = (): void => {},
  onHoverArea = (): void => {},
  onHoverAreaEnd = (): void => {},
  onClickDrawing = (): void => {},
  onEditDrawing = (): void => {},
  onDeleteDrawing = (): void => {},
  onShareObject = (): void => {},
  onCreateNewObject = (): void => {},
  activeDrawingId = '',
  selectedAreaFeatureNamesByAreaId = {},
  error,
  isLoading = false,
  selectedTab = 0,
  onChangeTab = (): void => {},
}) => {
  const { t } = useWarningsTranslation();
  const minSize = DEFAULT_OBJECT_MANAGER_MIN_SIZE;
  const startSizeCalc = calculateStartSize(minSize, size, startPosition);
  const [sizeInState, setSizeInState] =
    React.useState<DraggableSize>(startSizeCalc);

  const handleChangeTab = (
    event: React.SyntheticEvent,
    newValue: number,
  ): void => {
    onChangeTab(newValue);
  };

  return (
    <ToolContainerDraggable
      bounds={bounds}
      headerSize={headerSize}
      isOpen={isOpen}
      minHeight={minSize.height}
      minWidth={minSize.width}
      onClose={onClose}
      onDragEnd={(_position, dragSize): void => {
        if (dragSize !== sizeInState) {
          setSizeInState(dragSize as DraggableSize);
        }
      }}
      onMouseDown={onMouseDown}
      onResizeStop={(_event, _direction, node): void => {
        const { offsetWidth: width, offsetHeight: height } = node;
        setSizeInState({ width, height });
      }}
      order={order}
      source={source}
      startPosition={startPosition}
      startSize={sizeInState}
      sx={{
        footer: {
          backgroundColor: 'inherit',
          boxShadow: 'none',
        },
      }}
      title={title}
    >
      {!!isLoading && (
        <LinearProgress
          color="secondary"
          data-testid="loading-bar"
          sx={{ position: 'absolute', width: '100%', top: 0 }}
        />
      )}
      {!!error && (
        <Box>
          <AlertBanner shouldClose title={error} />
        </Box>
      )}
      <Box sx={{ pl: '8px', pr: '8px' }}>
        <Tabs
          onChange={handleChangeTab}
          sx={{
            '&.MuiTabs-root': {
              minHeight: `${DEFAULT_OBJECT_MANAGER_TAB_HEIGHT}px`,
            },
          }}
          value={selectedTab}
          variant="fullWidth"
        >
          {/* Areas: 0 */}
          <Tab label={t('object-manager-tab-areas')} sx={{ ...tabStyle }} />
          {/* Objects: 1 */}
          <Tab label={t('object-manager-tab-objects')} sx={{ ...tabStyle }} />
        </Tabs>
      </Box>
      {selectedTab === 0 && (
        <Areas
          activeArea={activeDrawingId}
          areas={predefinedAreaListItems}
          dialogWidth={sizeInState.width}
          isLoading={isLoading}
          onClickArea={onClickArea}
          onHoverArea={onHoverArea}
          onHoverAreaEnd={onHoverAreaEnd}
          selectedAreaFeatureNamesByAreaId={selectedAreaFeatureNamesByAreaId}
        />
      )}
      {selectedTab === 1 && (
        <Objects
          activeObject={activeDrawingId}
          dialogWidth={sizeInState.width}
          isLoading={isLoading}
          objects={drawingListItems}
          onClickDelete={onDeleteDrawing}
          onClickEdit={onEditDrawing}
          onClickObject={onClickDrawing}
          onClickShare={onShareObject}
        />
      )}
      <Box
        sx={{
          padding: '24px 8px 16px 8px',
          position: 'absolute',
          bottom: 0,
          width: '100%',
          backgroundColor: 'geowebColors.background.surfaceApp',
          boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.5)',
          zIndex: 1,
        }}
      >
        <Button
          onClick={onCreateNewObject}
          startIcon={<DrawPolygon />}
          sx={{ width: '100%', textTransform: 'none', fontSize: '12px' }}
          variant="tertiary"
        >
          {t('object-manager-add')}
        </Button>
      </Box>
    </ToolContainerDraggable>
  );
};
